<template>
  <div :class="classes">
    <div v-if="label || description" class="field__details">
      <label v-if="label" :for="id" class="field__label">{{ label }}</label>
      <p v-if="hasDescription" class="field__description">
        <EIcon v-if="disabled" class="field__disabled-icon" icon="exclamation-circle" size="s" />
        <slot name="description" />
      </p>
    </div>
    <div class="field__input-wrapper">
      <component
        :is="inputType"
        v-model="fieldValue"
        class="field__input"
        v-bind="{ id, type, name, disabled, ...$attrs }"
        :aria-label="label"
        v-on="listeners"
      />
      <EError v-if="error" class="field__error" data-test-id="field-error">
        {{ error }}
      </EError>
    </div>
  </div>
</template>

<script>
import classes from '~~/common/vue/mixins/classes';
import ECheckbox from '~~/common/vue/components/checkbox';
import EInput from '~~/common/vue/components/input';
import ETextarea from '~~/common/vue/components/textarea';
import EError from '~~/common/vue/components/error';
import ERadios from '~~/common/vue/components/radios';
import EIcon from '~~/common/vue/components/icon';
import ESelect from '~~/common/vue/components/select';

export const ALLOWED_TYPES = [
  'text',
  'url',
  'tel',
  'email',
  'password',
  'radio',
  'select',
  'textarea',
  'checkbox',
];

/**
 * A generic form field
 */
export default {
  name: 'EField',
  components: {
    EError,
    EIcon,
  },
  mixins: [classes],
  props: {
    type: {
      type: String,
      default: 'text',
      validator: (value) => ALLOWED_TYPES.indexOf(value) > -1,
      cssClass: true,
    },
    value: {
      type: [String, Number, Boolean, Object],
      default: null,
    },
    error: {
      type: String,
      default: null,
      cssClass: true,
    },
    label: {
      type: [String, null],
      default: null,
    },
    /**
     * Will be mapped as the `name` attribute of the form input
     */
    name: {
      type: String,
      required: true,
    },
    description: {
      type: [String, null],
      default: null,
    },
    forceStacked: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    disabled: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
  },
  data() {
    return {
      fieldValue: this.value,
    };
  },
  computed: {
    inputType() {
      switch (this.type) {
        case 'checkbox':
          return ECheckbox;
        case 'radio':
          return ERadios;
        case 'select':
          return ESelect;
        case 'textarea':
          return ETextarea;
        default:
          return EInput;
      }
    },
    listeners() {
      const { input, ...rest } = this.$listeners;
      return rest;
    },
    id() {
      return `e-field-${this.name}`;
    },
    // TODO: extract this technique into a general-purpose function
    hasDescription() {
      return !!this.$slots.description && !!this.$slots.description[0];
    },
  },
  watch: {
    fieldValue(val) {
      this.$emit('input', val);
    },
    value(val) {
      this.fieldValue = val;
    },
  },
};
</script>

<style lang="scss" scoped>
.field {
  &:not(.field--force-stacked) {
    &.field--type-checkbox,
    &.field--type-radio {
      display: flex;
      justify-content: space-between;
    }

    @include tablet {
      display: flex;
      justify-content: space-between;
    }
  }

  & + & {
    margin-top: $space-1l;

    @include tablet {
      margin-top: $space-l;
    }
  }

  &__error {
    margin-top: $space-1s;
  }
}

.field__input-wrapper {
  width: $form-field-input-max-width;

  .field--force-stacked & {
    width: inherit;
  }

  .field--type-checkbox &,
  .field--type-radio & {
    align-items: flex-start;
    display: flex;
    flex-shrink: 1;
    width: auto;

    @include tablet {
      align-items: center;
      order: 1;
    }
  }
}

.field__details {
  margin: 0 0 $space-m;
  max-width: $form-field-details-max-width;

  @include tablet {
    margin-bottom: 0;
    padding-right: $space-2l;

    .field--force-stacked & {
      margin-bottom: $space-m;
    }
  }

  .field--type-checkbox &,
  .field--type-radio & {
    flex-grow: 1;
    margin-bottom: 0;
    margin-left: $space-m;
    max-width: none;
    order: 1;

    @include tablet {
      margin-left: 0;
      order: 0;
    }
  }
}

.field__label {
  font-size: $font-l;

  .field--disabled & {
    color: $color-text-secondary;
  }
}

.field__description {
  color: $color-text-secondary;
  font-size: $font-m;
  margin: $space-2s 0 0;

  .field--disabled & {
    display: flex;

    .field__disabled-icon {
      flex-shrink: 0;
      margin-right: $space-1s;
    }
  }
}
</style>

<docs>
```js
let value = false;
<div>
  <EField
    type="select"
    :options="['Option 1', 'Option 2', 'Option 3']"
    placeholder="Choose an option"
    name="test"
    label="Field Label"
    required
  >
    <template v-slot:description>
      <div>This is the field description.</div>
    </template>
  </EField>
  <EField
    type="checkbox"
    placeholder="Choose an option"
    v-model="value"
    name="test-checkbox"
    label="Checkbox Field"
    required
  >
    <template v-slot:description>
      <div>This is the field description.</div>
    </template>
  </EField>
  <EField
    type="text"
    placeholder="Type some text"
    name="text-field"
    label="Disabled Field"
    disabled="true"
  >
    <template v-slot:description>
      <div>
        This is a field description with a warning.
        Ullamco aliqua voluptate laborum nostrud eu enim consectetur
        in eiusmod minim ex laboris Lorem.
      </div>
    </template>
  </EField>
</div>
```

#### Force stacking

For the `textarea` input type, it is recommended to add `force-stacked`
so the label appears above.

```js
let value;
<div>
  <EField
    type="textarea"
    v-model="value"
    name="test"
    label="A Text Area"
    required
    :force-stacked="true"
  />
  <br/>
  The value is {{value}}
</div>
```

#### Invalid field

```js
let value;
// The invalid state of the input itself is a separate prop!
<div>
  <EField
    invalid
    error="This is an Error"
    type="text"
    label="An invalid field"
    name="test" />
</div>
```

</docs>
